import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
} from 'recharts';
import { climbCategoryColor } from './format';

// Interactive elevation profile. X axis is distance (km), Y axis altitude (m).
// Detected climbs are highlighted as shaded bands.
export default function ElevationChart({ samples, climbs, dark }) {
  const data = useMemo(() => {
    if (!samples || samples.length === 0) return [];
    return samples.map((s, i) => {
      let grade = 0;
      if (i > 0) {
        const dDist = s.dist - samples[i - 1].dist;
        if (dDist > 0) grade = ((s.ele - samples[i - 1].ele) / dDist) * 100;
      }
      return {
        km: +(s.dist / 1000).toFixed(2),
        ele: Math.round(s.ele),
        grade: +grade.toFixed(1),
      };
    });
  }, [samples]);

  const axisColor = dark ? '#a0aec0' : '#4a5568';
  const gridColor = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  if (data.length === 0) {
    return (
      <Box h="180px" display="flex" alignItems="center" justifyContent="center">
        <Text color="gray.400" fontSize="sm">
          Perfil de elevacion no disponible
        </Text>
      </Box>
    );
  }

  return (
    <Box w="100%" h={['200px', null, '240px']}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="eleFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#dba800" stopOpacity={0.85} />
              <stop offset="100%" stopColor="#dba800" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={gridColor} vertical={false} />
          <XAxis
            dataKey="km"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v) => `${Math.round(v)}`}
            tick={{ fill: axisColor, fontSize: 11 }}
            stroke={gridColor}
            unit=" km"
          />
          <YAxis
            tick={{ fill: axisColor, fontSize: 11 }}
            stroke={gridColor}
            width={44}
            unit=" m"
          />
          <Tooltip
            contentStyle={{
              background: dark ? '#1a202c' : '#ffffff',
              border: 'none',
              borderRadius: 8,
              fontSize: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            }}
            labelStyle={{ color: axisColor }}
            formatter={(value, name) => {
              if (name === 'ele') return [`${value} m`, 'Altitud'];
              return [value, name];
            }}
            labelFormatter={(label) => `Km ${label}`}
          />
          {(climbs || []).map((c, i) => (
            <ReferenceArea
              key={i}
              x1={+(c.startDist / 1000).toFixed(2)}
              x2={+(c.endDist / 1000).toFixed(2)}
              fill={climbCategoryColor(c.category)}
              fillOpacity={0.15}
              stroke="none"
            />
          ))}
          <Area
            type="monotone"
            dataKey="ele"
            stroke="#dba800"
            strokeWidth={2}
            fill="url(#eleFill)"
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}
